import React, { useState, useRef, useEffect } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';

type Primitive = string | number;

export interface D3BarChartDatum {
  key: Primitive;
  value: number;
}

interface D3BarChartProps {
  data: D3BarChartDatum[];
  width?: number;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  title?: string;
  xLabel?: string;
  yLabel?: string;
  selectedKeys?: Primitive[];
  onBarClick?: (key: Primitive) => void;
  formatXTick?: (k: Primitive) => string;
  formatYTick?: (n: number) => string;
}

interface TooltipData {
  key: Primitive;
  value: number;
  x: number;
  y: number;
}

export const D3BarChart: React.FC<D3BarChartProps> = ({
  data,
  width: propWidth,
  height: propHeight = 320,
  margin: propMargin,
  title,
  xLabel,
  yLabel,
  selectedKeys = [],
  onBarClick,
  formatXTick = (k) => String(k),
  formatYTick = (n) => String(n),
}) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(500);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (containerRef.current) {
        const computedStyle = window.getComputedStyle(containerRef.current);
        const paddingLeft = parseFloat(computedStyle.paddingLeft);
        const paddingRight = parseFloat(computedStyle.paddingRight);
        const width = containerRef.current.offsetWidth - paddingLeft - paddingRight;
        setContainerWidth(width);
        setIsMobile(width < 500);
      }
    };
    
    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);
  
  const margin = propMargin || (isMobile 
    ? { top: 12, right: 12, bottom: 32, left: 58 }
    : { top: 16, right: 24, bottom: 36, left: 60 }
  );
  
  if (!data?.length) return null;

  const width = propWidth || containerWidth;
  const height = propHeight;

  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  const maxValue = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  const xDomain = data.map((d) => d.key);

  const xScale = scaleBand<Primitive>()
    .domain(xDomain)
    .range([0, innerW])
    .padding(0.15);

  const yScale = scaleLinear()
    .domain([0, maxValue])
    .range([innerH, 0])
    .nice();

  const fontSize = isMobile ? 10 : 12;
  const labelFontSize = isMobile ? 10 : 11;
  const titleFontSize = isMobile ? 12 : 14;
  const tickCount = isMobile ? 4 : 6;

  return (
    <div ref={containerRef} className="bedrooms-chart-container" style={{ position: 'relative', width: '100%' }}>
      {title ? <h3>{title}</h3> : null}
      <div style={{ overflowX: 'auto' }}>
        <svg width={width} height={height}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {data.map((d) => {
            const x = (xScale(d.key) ?? 0);
            const barWidth = xScale.bandwidth();
            const value = Number(d.value) || 0;
            const y = yScale(value);
            const h = innerH - y;
            const isActive = selectedKeys.includes(d.key);
            const isFiltered = selectedKeys.length > 0 && !isActive;
            const labelY = Math.max(y - 6, 12);
            return (
              <g key={String(d.key)}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={h}
                  fill={isActive ? '#1d4ed8' : '#2979FF'}
                  opacity={isFiltered ? 0.35 : 0.8}
                  stroke="#2d3748"
                  strokeWidth={1}
                  rx={2}
                  style={{ 
                    cursor: onBarClick ? 'pointer' : 'default', 
                    transition: 'y 400ms, height 400ms, opacity 200ms, fill 200ms, filter 200ms, transform 200ms',
                    transformOrigin: `${x + barWidth/2}px ${y + h}px`
                  }}
                  onClick={() => onBarClick && onBarClick(d.key)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.2)';
                    e.currentTarget.style.transform = 'scale(1.01)';
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltip({
                      key: d.key,
                      value,
                      x: rect.left + rect.width / 2,
                      y: rect.top,
                    });
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'brightness(1)';
                    e.currentTarget.style.transform = 'scale(1)';
                    setTooltip(null);
                  }}
                />
                <text
                  x={x + barWidth / 2}
                  y={labelY}
                  textAnchor="middle"
                  fontSize={labelFontSize}
                  fill="#e2e8f0"
                  fontWeight={600}
                  style={{ pointerEvents: 'none' }}
                >
                  {Math.round(value)}
                </text>
              </g>
            );
          })}

          {/* X axis */}
          <g transform={`translate(0, ${innerH})`}>
            <line x1={0} x2={innerW} stroke="#4a5568" strokeWidth={1} />
            {xDomain.map((k) => {
              const cx = (xScale(k) ?? 0) + xScale.bandwidth() / 2;
              return (
                <g key={`xtick-${String(k)}`} transform={`translate(${cx},0)`}>
                  <line y1={0} y2={6} stroke="#4a5568" />
                  <text
                    y={18}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fill="#a0aec0"
                    fontWeight={500}
                  >
                    {formatXTick(k)}
                  </text>
                </g>
              );
            })}
            {xLabel ? (
              <text
                x={innerW / 2}
                y={isMobile ? 30 : 34}
                textAnchor="middle"
                fontSize={titleFontSize}
                fill="#e2e8f0"
                fontWeight={600}
              >
                {xLabel}
              </text>
            ) : null}
          </g>

          {/* Y axis */}
          <g>
            <line y1={0} y2={innerH} stroke="#4a5568" strokeWidth={1} />
            {yScale.ticks(tickCount).map((tick) => (
              <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
                <line x2={-6} stroke="#4a5568" />
                <text x={-10} textAnchor="end" dy="0.35em" fontSize={fontSize} fill="#a0aec0" fontWeight={500}>
                  {formatYTick(tick)}
                </text>
              </g>
            ))}
            {yLabel ? (
              <text
                x={isMobile ? -45 : -50}
                y={innerH / 2}
                textAnchor="middle"
                fontSize={titleFontSize}
                fill="#e2e8f0"
                fontWeight={600}
                transform={`rotate(-90, ${isMobile ? -45 : -50}, ${innerH / 2})`}
              >
                {yLabel}
              </text>
            ) : null}
          </g>
        </g>
      </svg>
      </div>
      
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y - 10,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(30, 41, 59, 0.95)',
            color: '#e2e8f0',
            padding: isMobile ? '6px 10px' : '8px 12px',
            borderRadius: '6px',
            fontSize: isMobile ? '11px' : '13px',
            fontWeight: 500,
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(71, 85, 105, 0.5)',
            lineHeight: '1.5',
          }}
        >
          <div>{xLabel || 'Categoria'}: {formatXTick(tooltip.key)}</div>
          <div>{yLabel || 'Valor'}: {formatYTick(tooltip.value)}</div>
        </div>
      )}
    </div>
  );
};


